import CustomLink from "components/CustomLink";
import CustomTable from "components/CustomTable";
import ExportReactCSV from "components/ExportReactCSV";
import { CustomStatusBadge } from "components/StatusBadge";
import PaperButton from "components/buttons/PaperButton";
import CustomText from "components/typography/CustomText";
import { formatArray } from "lib/arrayObject";
import { getWeekDate, getWeekDaysByWeekNumber } from "lib/date";
import { currency } from "lib/formatter";
import {
  DELIVERY_FEE,
  orderStatusOptions,
  paymentStatusOption,
} from "lib/global";
import { toNumber } from "lodash";
import moment from "moment";

export default function OneOffOrdersTable({
  data = [],
  onLoadData = () => {},
  totalPage = 0,
  page = 1,
}) {
  const headers = [
    { label: "Order ID", key: "id" },
    { label: "Created", key: "created" },
    { label: "Customer", key: "name" },
    { label: "Value", key: "amount" },
    { label: "Dispatch Date", key: "dispatch_date" },
    { label: "Address", key: "address" },
    { label: "Payment", key: "payment" },
    { label: "Status", key: "status" },
  ];

  const oneOffOrders = () => {
    let custs = data.map((row) => ({
      id: row?.weekly_slot_id ?? "",
      created: moment(row?.created_at).format("DD/MM/YY") ?? "",
      name: row?.name ?? "",
      amount:
        currency.format(
          formatArray(row?.pies).reduce(
            (ret, cur) => ret + toNumber(cur?.price_oneoff ?? 0),
            15
          )
        ) ?? "",
      frequency: `${row?.lastWeeklySlot?.frequency} weeks`,
      dispatch_date: getWeekDaysByWeekNumber({
        weekNumber:
          row?.lastWeeklySlot?.week_next_num ??
          row?.lastWeeklySlot?.week_num ??
          1,
        addDay: row?.lastWeeklySlot?.week_day - 1,
        format: "DD/MM/YY",
        yearNumber: row?.lastWeeklySlot?.week_year,
      }),
      address: row?.address?.label ?? "",
      payment:
        paymentStatusOption?.find(
          (item) => item.id === row?.lastWeeklySlot?.paid
        )?.label ?? "",
      status:
        orderStatusOptions?.find(
          (item) => item.id === row?.lastWeeklySlot?.status
        )?.label ?? "",
    }));
    return custs;
  };

  const handleChangePage = (p, sortKey) => {
    onLoadData({ page: p, sortKey });
  };

  const handleSortKey = (sortKey) => {
    onLoadData({ sortKey, page });
  };

  return (
    <div className="pb-8">
      <CustomTable
        data={data}
        height={1000}
        pageCount={50}
        isPageActive={true}
        page={page}
        totalPage={totalPage}
        onChangePage={handleChangePage}
        onSortKey={handleSortKey}
        toolbar={
          <div className="p-4 border-b border-white-gray flex justify-between items-center">
            <CustomText variant="h6" bold={600}>
              One-off Orders
            </CustomText>
            <ExportReactCSV
              csvHeaders={headers}
              csvData={oneOffOrders()}
              fileName={`One-off Orders.csv`}
            />
          </div>
        }
        columns={[
          {
            label: "ORDER ID",
            selector: "weekly_slot_id",
            cell: (row) => (
              <CustomLink to={`/one_off_order/${row?.weekly_slot_id ?? ""}`}>
                <CustomText
                  className="text-13"
                  variant="text"
                  color="primary"
                  bold={600}
                >
                  {row?.weekly_slot_id}
                </CustomText>
                {/* <PaperButton
                  // onClick={() => onView(row, rowIndex)}
                  variant="text"
                  className="text-13"
                  color="primary"
                >
                  View
                </PaperButton> */}
              </CustomLink>
            ),
          },
          {
            label: "CREATED",
            selector: "created_at",
            sortValue: (row) => moment(row?.created_at).unix(),
            cell: (row) => (
              <CustomText className="text-13">
                {moment(row?.created_at).format("DD/MM/YY")}
              </CustomText>
            ),
          },
          {
            label: "CUSTOMER",
            selector: "name",
          },
          {
            label: "VALUE",
            selector: "amount",
            sortValue: (row) =>
              formatArray(row?.pies).reduce(
                (ret, cur) => ret + toNumber(cur?.price_oneoff ?? 0),
                0
              ),
            cell: (row) => (
              <CustomText className="text-13">
                {currency.format(
                  formatArray(row?.pies).reduce(
                    (ret, cur) => ret + toNumber(cur?.price_oneoff ?? 0),
                    0
                  ) + DELIVERY_FEE
                )}
              </CustomText>
            ),
          },
          {
            label: "DISPATCH DATE",
            selector: "fulfillment",
            sortValue: (row) =>
              getWeekDate({
                weekNumber:
                  row?.lastWeeklySlot?.week_next_num ??
                  row?.lastWeeklySlot?.week_num ??
                  1,
                addDay: row?.lastWeeklySlot?.week_day - 1,
                yearNumber: row?.lastWeeklySlot?.week_year,
              }).unix(),
            cell: (row) => (
              <CustomText className="text-13">
                {getWeekDaysByWeekNumber({
                  weekNumber:
                    row?.lastWeeklySlot?.week_next_num ??
                    row?.lastWeeklySlot?.week_num ??
                    1,
                  addDay: row?.lastWeeklySlot?.week_day - 1,
                  format: "DD/MM/YY",
                  yearNumber: row?.lastWeeklySlot?.week_year,
                })}
              </CustomText>
            ),
          },
          {
            label: "ADDRESS",
            selector: "address",
            sortValue: (row) => row?.address?.label,
            cell: (row) => (
              <CustomText
                className="text-13"
                variant="text"
                color="primary"
                bold={600}
              >
                {row?.address?.label ?? ""}
              </CustomText>
            ),
          },
          {
            label: "Payment",
            selector: "payment",
            sortValue: (row) =>
              paymentStatusOption?.find(
                (item) => item.id === row?.lastWeeklySlot?.paid
              )?.label ?? "",
            cell: (row) => (
              <div className="flex items-center">
                <CustomStatusBadge
                  size="body"
                  variant={
                    paymentStatusOption?.find(
                      (item) => item.id === row?.lastWeeklySlot?.paid
                    )?.variant ?? ""
                  }
                  text={
                    paymentStatusOption?.find(
                      (item) => item.id === row?.lastWeeklySlot?.paid
                    )?.label ?? ""
                  }
                />
              </div>
            ),
          },
          {
            label: "Status",
            selector: "status",
            sortValue: (row) =>
              orderStatusOptions?.find(
                (item) => item.id === row?.lastWeeklySlot?.status
              )?.label ?? "",
            cell: (row) => (
              <div className="flex items-center">
                <CustomStatusBadge
                  size="body"
                  variant={
                    orderStatusOptions?.find(
                      (item) => item.id === row?.lastWeeklySlot?.status
                    )?.variant ?? ""
                  }
                  text={
                    orderStatusOptions?.find(
                      (item) => item.id === row?.lastWeeklySlot?.status
                    )?.label ?? ""
                  }
                />
              </div>
            ),
          },
          {
            label: "",
            selector: "actions",
            cell: (row, rowIndex) => (
              <CustomLink to={`/one_off_order/${row?.weekly_slot_id ?? ""}`}>
                <PaperButton
                  // onClick={() => onView(row, rowIndex)}
                  variant="text"
                  className="text-13"
                  color="primary"
                >
                  View
                </PaperButton>
              </CustomLink>
            ),
          },
        ]}
      />
    </div>
  );
}
